import React, { type FC, type PropsWithChildren } from 'react';

import { withImage } from '@lucid/hooks';

import type { Lucid } from '../../types/types';
import Box from '../box/box';
import Container from '../container/container';
import Heading from '../heading/heading';
import Icon from '../icon/icon';
import ImageWithPlaceholder from '../image/image-with-placeholder';
import Link from '../link/link';
import Text from '../text/text';
import type { ComponentImage } from '../../types/generics';
import QuoteCarousel from './quote-carousel/quote-carousel';

export interface QuoteCompanyProps {
  /** The name of the company. */
  name?: string | null;
  /** The logo of the company. */
  logo?: ComponentImage;
}

export interface QuoteCtaLinkType {
  /** Checks if the Link should recieve the Button styles. */
  displayAsButton?: boolean | null;
  /** The title for the Link/Button. */
  title?: string | null;
  /** The Link/Button url path. */
  url?: {
    path?: string | null;
  };
}

export interface QuoteProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** List of quotes to include in the carousel */
  quotes?: Lucid.Directus.Generated.Quote[];
  /** List of quotes to include in the carousel */
  cta?: Lucid.Directus.Generated.Links;
  /** Variant for links in quote */
  linkVariant?: Lucid.Theme.Component.Link.VariantKeys | Lucid.Theme.Component.Button.VariantKeys;
  /** Typography variant for the quote text */
  quoteTextVariant?: Lucid.Theme.Component.Typography.Paragraph.VariantKeys;
  /** The color of the Quote icons */
  iconColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
}

const Quote: FC<PropsWithChildren<QuoteProps>> = ({
  className,
  quotes,
  cta,
  linkVariant,
  quoteTextVariant,
  iconColor,
  ...rest
}) => {
  if (!quotes || quotes?.length < 1) return null;
  const individualQuote = quotes?.length === 1 ? quotes?.[0] : null;
  const translatedData = individualQuote?.translations?.[0];
  const fullName = individualQuote?.full_name;
  const company = individualQuote?.company;
  const companyLogo = individualQuote?.company_logo;

  return (
    <Box
      {...rest}
      css={theme => ({
        label: 'quote',
        padding: `${theme.space.s32}px 0`,
        [theme.breakpoints.bp1024.min]: {
          padding: `${theme.space.s80}px 0`,
        },
      })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      {quotes && quotes.length > 1 ? (
        <QuoteCarousel linkVariant={linkVariant} quotes={quotes} quoteTextVariant={quoteTextVariant} />
      ) : (
        <Box>
          <Container
            as="section"
            css={theme => ({
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginBottom: '32px',
              [theme.breakpoints.bp1024.min]: {
                paddingRight: 0,
                paddingLeft: 0,
                marginBottom: 0,
              },
            })}
          >
            <figure css={theme => ({ margin: `${theme.space.s48}px 0` })}>
              {companyLogo && (fullName || companyLogo) && (
                <Box
                  css={theme => ({
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    paddingBottom: theme.space.s64,
                    [theme.breakpoints.bp1024.min]: {
                      textAlign: 'start',
                      alignItems: 'start',
                      paddingBottom: theme.space.s24,
                    },
                  })}
                >
                  {companyLogo && (
                    <Box
                      css={theme => ({
                        paddingBottom: theme.space.s16,
                        width: '100%',
                        maxWidth: '170px',
                        height: '100%',
                      })}
                    >
                      <ImageWithPlaceholder
                        alt={companyLogo.translations?.[0]?.alt || companyLogo.title || ''}
                        css={{
                          maxWidth: '170px',
                        }}
                        src={withImage(companyLogo)}
                      />
                    </Box>
                  )}
                  {fullName && (
                    <Heading as="h2" variant="headingM">
                      {fullName}
                    </Heading>
                  )}
                  {translatedData?.position && (
                    <Heading as="h3" variant="headingM">
                      {translatedData?.position}
                    </Heading>
                  )}
                </Box>
              )}
              <Box
                css={theme => ({
                  [theme.breakpoints.bp1024.min]: {
                    position: 'relative',
                    maxWidth: '680px',
                  },
                })}
              >
                <Icon
                  color={iconColor || 'primary'}
                  css={theme => ({
                    [theme.breakpoints.bp1024.min]: {
                      position: 'absolute',
                      top: '-16px',
                      left: '-96px',
                    },
                  })}
                  type="StartQuote"
                />
                <Text
                  as="blockquote"
                  css={theme => ({
                    marginInline: 0,
                    paddingTop: theme.space.s32,
                    paddingBottom: theme.space.s32,
                    marginBottom: 0,
                    fontSize: theme.fontSizes.fs24,
                    [theme.breakpoints.bp1024.min]: {
                      paddingTop: 'unset',
                      paddingBottom: theme.space.s32,
                    },
                  })}
                  variant="paragraphM300"
                >
                  {translatedData?.text}
                </Text>
                <Icon
                  color={iconColor || 'primary'}
                  css={theme => ({
                    position: 'absolute',
                    right: '100px',
                    bottom: '32px',
                    [theme.breakpoints.bp1024.min]: {
                      bottom: '32px',
                      right: '-32px',
                    },
                  })}
                  type="EndQuote"
                />
              </Box>
              {!companyLogo && (fullName || translatedData?.position) && (
                <figcaption css={theme => ({ margin: `${theme.space.s48}px 0 0` })}>
                  <Text as="span" css={{ fontWeight: 700 }} role="heading" variant="paragraphM300">
                    {`${fullName ? `- ${fullName}` : ''} ${
                      translatedData?.position ? `- ${translatedData?.position} ${company ? ` ${company}` : ''}` : ''
                    }`}
                  </Text>
                </figcaption>
              )}
            </figure>
          </Container>
          <Container
            css={theme => ({
              maxWidth: '680px',
              margin: 'unset',
              marginBottom: theme.space.s48,
              [theme.breakpoints.bp1024.min]: {
                margin: '0 auto',
                marginBottom: theme.space.s48,
                paddingRight: 0,
                paddingLeft: 0,
              },
            })}
          >
            {cta && (
              <Link href={cta?.final_url} iconAnimation="navigational" variant={linkVariant}>
                {cta.default_link_text}
              </Link>
            )}
          </Container>
        </Box>
      )}
    </Box>
  );
};

export { Quote };
export default Quote;
